import { useRef } from 'react'

import { Box } from '@factorialco/deprecated-design-system'

import TextEditor, { type EditorHandle } from 'modules/core/components/TextEditor'
import type { MentionChangeResult } from 'modules/core/components/TextEditor/Editor'
import TextField from 'modules/core/components/TextField'
import i18n from 'modules/core/lib/i18n'

import Assignees from '../../components/Form/Items/Assignees'
import { VariablesSelectorComponent } from './VariablesSelector'
import { useStepActionDescription } from './useStepActionDescription'

/**
 * Limited to 16 MB to avoid service disruptions when sending emails.
 *
 * For more on why this limit exists see [this retro](https://www.notion.so/factorialco/Onboarding-step-with-video-attachment-consuming-Redis-memory-1385e6e051ee8067a0d7dd846b15bda6?pvs=4).
 */
const MAX_ALLOWED_FILE_SIZE_MB = 16

export interface StateProps {
  subject: string
  body: string | MentionChangeResult
  files?: File[]
}

interface Props {
  subject: string
  body: string | MentionChangeResult
  files?: File[]
  updateSubject: (value: string) => void
  updateBody: (value: string | MentionChangeResult | null) => void
  readonly?: boolean
  onFiles: (files: File[]) => void
  addVariable: (variable: string) => void
}

export const EditorContent = ({
  subject,
  body,
  files,
  onFiles,
  updateSubject,
  updateBody,
  readonly,
  addVariable,
}: Props) => {
  const editorRef = useRef<EditorHandle>(null)
  const description = useStepActionDescription()
  const textEditorData =
    body?.toString() || i18n.t('processes.process_settings.actions.automatic_email.text')

  return (
    <Box paddingTop="s24" gap="s24" paddingRight="s16">
      <Assignees
        readonly={readonly}
        label={i18n.t('processes.process_settings.actions.automatic_email.recipient')}
      />
      <TextField
        readonly={readonly}
        mandatory
        attachmentPosition="middle right"
        label={i18n.t('processes.process_settings.actions.automatic_email.subject.title')}
        placeholder={i18n.t(
          'processes.process_settings.actions.automatic_email.subject.placeholder'
        )}
        value={subject}
        onChange={updateSubject}
      />
      <TextField
        readonly
        label={i18n.t('processes.process_settings.actions.automatic_email.trigger')}
        value={description}
      />
      {files && (
        <div>
          <VariablesSelectorComponent onSelect={addVariable} />
          <TextEditor
            ref={editorRef}
            readonly={readonly}
            initialEditorState={{
              data: textEditorData,
              files: files || [],
            }}
            maxFileSize={MAX_ALLOWED_FILE_SIZE_MB}
            onFiles={onFiles}
            placeholder={i18n.t('processes.process_settings.actions.automatic_email.text')}
            onChange={(html: string | MentionChangeResult | null) => {
              if (!html) return
              updateBody(html?.toString())
            }}
            height="s300"
          />
        </div>
      )}
    </Box>
  )
}
